import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, BarChart3, Zap, Target, Layers } from 'lucide-react';

const TechMetricDiagram: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const technologies = [
    { name: '机器学习', value: 95, color: 'bg-serchon-gold' },
    { name: '深度学习', value: 90, color: 'bg-blue-500' },
    { name: '数据处理', value: 88, color: 'bg-purple-500' },
    { name: '云原生架构', value: 85, color: 'bg-green-500' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % technologies.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [technologies.length]);

  return (
    <div className="flex flex-col md:flex-row gap-8 items-center p-8 bg-stone-900 text-stone-100 rounded-xl my-8 border border-stone-800 shadow-lg">
      <div className="flex-1 min-w-[240px]">
        <h3 className="font-serif text-xl mb-2 text-serchon-gold">核心技术能力</h3>
        <p className="text-stone-400 text-sm mb-4 leading-relaxed">
          我们在人工智能、深度学习、数据处理等领域拥有深厚的技术积累，为客户提供卓越的解决方案。
        </p>
        <div className="flex gap-2 mt-6">
          {technologies.map((tech, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 border ${
                activeIndex === index
                  ? 'bg-serchon-gold text-stone-900 border-serchon-gold'
                  : 'bg-transparent text-stone-400 border-stone-700 hover:border-stone-500 hover:text-stone-200'
              }`}
            >
              {tech.name}
            </button>
          ))}
        </div>
        <div className="mt-6 font-mono text-xs text-stone-500 flex items-center gap-2">
          <BarChart size={14} className="text-serchon-gold" />
          <span>技术能力指数（越高越好）</span>
        </div>
      </div>

      <div className="relative w-64 h-72 bg-stone-800/50 rounded-xl border border-stone-700/50 p-6 flex justify-around items-end">
        {technologies.map((tech, index) => (
          <motion.div
            key={index}
            className="w-12 flex flex-col justify-end items-center h-full z-10"
            initial={{ opacity: 0.5 }}
            animate={{ opacity: activeIndex === index ? 1 : 0.5 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex-1 w-full flex items-end justify-center relative mb-3">
              <div className="absolute -top-5 w-full text-center text-sm font-mono text-stone-400 font-bold bg-stone-900/90 py-1 px-2 rounded backdrop-blur-sm border border-stone-700/50 shadow-sm">
                {tech.value}%
              </div>
              <motion.div
                className={`w-full rounded-t-md border-t border-x border-stone-500/30 ${tech.color}`}
                initial={{ height: 0 }}
                animate={{
                  height: `${(tech.value / 100) * 100}%`,
                  scale: activeIndex === index ? 1.05 : 1,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 80,
                  damping: 15,
                  delay: index * 0.1,
                }}
              />
            </div>
            <div className="h-6 flex items-center text-[10px] font-bold text-stone-500 uppercase tracking-wider">
              {tech.name}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const TechnologySection: React.FC = () => {
  return (
    <section id="technology" className="py-24 bg-[#F9F8F4]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block mb-4 text-xs font-bold tracking-widest text-serchon-gold uppercase">
              核心技术
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mb-6 text-stone-900">技术驱动创新</h2>
            <p className="text-lg text-stone-600 max-w-3xl mx-auto">
              我们专注于前沿技术的研究与应用，为客户提供最先进的技术解决方案
            </p>
          </motion.div>
        </div>

        <TechMetricDiagram />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {[
            {
              icon: <Zap className="w-8 h-8" />,
              title: '快速响应',
              description: '高效的技术架构，确保快速交付和部署',
            },
            {
              icon: <Target className="w-8 h-8" />,
              title: '精准定位',
              description: '深入理解业务需求，提供定制化解决方案',
            },
            {
              icon: <Layers className="w-8 h-8" />,
              title: '多层架构',
              description: '模块化设计，易于扩展和维护',
            },
            {
              icon: <BarChart3 className="w-8 h-8" />,
              title: '数据驱动',
              description: '基于数据分析优化产品性能和用户体验',
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm hover:shadow-md transition-all"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="text-serchon-gold mb-4">{item.icon}</div>
              <h3 className="font-serif text-lg font-bold text-stone-900 mb-2">{item.title}</h3>
              <p className="text-sm text-stone-600">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechnologySection;
